<template>
  <div class="storyboard-script-view">
    <div class="storyboard-script-toolbar">
      <div>
        <strong>镜头卡片</strong>
        <p>每个镜头独立成卡片，可直接新增、编辑、删除；保存后可继续走底部审批或重新生成。</p>
      </div>
      <div class="result-actions">
        <el-button plain @click="emit('add-scene')">添加分镜</el-button>
      </div>
    </div>

    <div class="storyboard-script-grid">
      <div
        v-for="(scene, index) in scenes"
        :key="scene._clientId"
        class="storyboard-script-card-shell"
        :class="{
          'storyboard-script-card-shell--active': activeSceneNumber === scene.scene_number,
          'storyboard-script-card-shell--editing': editingSceneNumber === scene.scene_number,
        }"
        @mouseenter="emit('hover-scene', scene.scene_number)"
        @mouseleave="emit('hover-scene', null)"
      >
        <button
          type="button"
          class="storyboard-insert-button storyboard-insert-button--top"
          :aria-label="`在镜头 ${scene.scene_number} 前插入分镜`"
          @click.stop="emit('insert-scene', { sceneNumber: scene.scene_number, position: 'before' })"
        >
          <span class="storyboard-insert-button__icon">+</span>
        </button>

        <article
          class="storyboard-script-card"
          :class="{
            'storyboard-script-card--editing': editingSceneNumber === scene.scene_number,
            'storyboard-script-card--active': activeSceneNumber === scene.scene_number,
          }"
          @click="emit('select-scene', scene.scene_number)"
        >
          <div class="storyboard-script-card__header">
            <div class="storyboard-script-card__badges">
              <span class="storyboard-script-card__badge storyboard-script-card__badge--scene">镜头{{ scene.scene_number }}</span>
              <span
                v-for="label in buildSceneMeta(scene)"
                :key="`${scene.scene_number}-${label}`"
                class="storyboard-script-card__badge"
              >
                {{ label }}
              </span>
            </div>
            <div class="storyboard-script-card__actions">
              <el-button link type="primary" @click.stop="emit('toggle-edit', scene.scene_number)">
                {{ editingSceneNumber === scene.scene_number ? '完成' : '编辑' }}
              </el-button>
              <el-button link type="danger" @click.stop="emit('delete-scene', scene.scene_number)">删除</el-button>
            </div>
          </div>

          <div v-if="editingSceneNumber === scene.scene_number" class="storyboard-script-card__editor">
            <el-input v-model="scene.location" placeholder="镜头标题或地点" @input="emit('mark-dirty')" />
            <div class="storyboard-script-card__grid">
              <el-input v-model="scene.shot_type" placeholder="景别，例如全景/近景" @input="emit('mark-dirty')" />
              <el-input v-model="scene.camera_angle" placeholder="角度，例如平视/俯视" @input="emit('mark-dirty')" />
              <el-input v-model="scene.camera_movement" placeholder="运镜，例如固定/推拉" @input="emit('mark-dirty')" />
            </div>
            <el-input
              v-model="scene.description"
              type="textarea"
              :rows="3"
              maxlength="600"
              show-word-limit
              placeholder="场景描述"
              @input="emit('mark-dirty')"
            />
            <el-input
              v-model="scene.action"
              type="textarea"
              :rows="4"
              maxlength="1000"
              show-word-limit
              placeholder="画面描述"
              @input="emit('mark-dirty')"
            />
            <el-input
              v-model="scene.dialogue_summary"
              type="textarea"
              :rows="3"
              maxlength="600"
              show-word-limit
              placeholder="对白"
              @input="emit('mark-dirty')"
            />
            <div class="storyboard-script-card__binding-grid">
              <el-select
                v-model="scene.official_character_asset_ids"
                multiple
                collapse-tags
                collapse-tags-tooltip
                clearable
                filterable
                placeholder="绑定官方角色"
                @change="emit('mark-dirty')"
              >
                <el-option
                  v-for="option in selectionOptions.character"
                  :key="`character-${option.value}`"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
              <el-select
                v-model="scene.official_scene_asset_id"
                clearable
                filterable
                placeholder="绑定官方场景"
                @change="emit('mark-dirty')"
              >
                <el-option
                  v-for="option in selectionOptions.scene"
                  :key="`scene-${option.value}`"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
              <el-select
                v-model="scene.official_prop_asset_ids"
                multiple
                collapse-tags
                collapse-tags-tooltip
                clearable
                filterable
                placeholder="绑定官方道具"
                @change="emit('mark-dirty')"
              >
                <el-option
                  v-for="option in selectionOptions.prop"
                  :key="`prop-${option.value}`"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </div>
            <div class="storyboard-script-card__editor-actions">
              <el-button @click.stop="emit('cancel-edit')">取消</el-button>
              <el-button type="primary" @click.stop="emit('finish-edit')">完成编辑</el-button>
            </div>
          </div>

          <div v-else class="storyboard-script-card__body">
            <div class="storyboard-script-card__title-row">
              <strong>{{ scene.location || `镜头 ${scene.scene_number}` }}</strong>
              <span>{{ scene.background_image_url ? '已绑定图片' : '待生成画面' }}</span>
            </div>
            <div class="storyboard-script-card__section">
              <span>场景描述</span>
              <p>{{ scene.description || '待补充分镜场景描述' }}</p>
            </div>
            <div class="storyboard-script-card__section">
              <span>画面描述</span>
              <p>{{ scene.action || scene.background_prompt || '待补充画面动作与镜头表达' }}</p>
            </div>
            <div class="storyboard-script-card__section">
              <span>对白</span>
              <p>{{ scene.dialogue_summary || '无' }}</p>
            </div>
            <div v-if="buildSceneBindingLabels(scene).length" class="storyboard-script-card__section">
              <span>官方绑定</span>
              <div class="storyboard-script-card__binding-tags">
                <span
                  v-for="label in buildSceneBindingLabels(scene)"
                  :key="`${scene.scene_number}-${label}`"
                  class="storyboard-script-card__binding-tag"
                >
                  {{ label }}
                </span>
              </div>
            </div>
          </div>
        </article>

        <button
          type="button"
          class="storyboard-insert-button storyboard-insert-button--bottom"
          :aria-label="index === scenes.length - 1 ? '在末尾新增分镜' : '在当前镜头后插入分镜'"
          @click.stop="emit('insert-scene', { sceneNumber: scene.scene_number, position: 'after' })"
        >
          <span class="storyboard-insert-button__icon">+</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ConsistencyAssetSelectionOptions } from './consistencyAssets'
import type { StoryboardSceneDraft } from './types'

defineProps<{
  scenes: StoryboardSceneDraft[]
  activeSceneNumber: number | null
  editingSceneNumber: number | null
  selectionOptions: ConsistencyAssetSelectionOptions
  buildSceneMeta: (scene: StoryboardSceneDraft) => string[]
  buildSceneBindingLabels: (scene: StoryboardSceneDraft) => string[]
}>()

const emit = defineEmits<{
  (event: 'add-scene'): void
  (event: 'insert-scene', payload: { sceneNumber: number; position: 'before' | 'after' }): void
  (event: 'hover-scene', value: number | null): void
  (event: 'select-scene', value: number): void
  (event: 'toggle-edit', value: number): void
  (event: 'delete-scene', value: number): void
  (event: 'mark-dirty'): void
  (event: 'cancel-edit'): void
  (event: 'finish-edit'): void
}>()
</script>

<style scoped>
.storyboard-script-view {
  display: grid;
  gap: 18px;
}

.storyboard-script-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.storyboard-script-toolbar strong {
  color: #0f172a;
}

.storyboard-script-toolbar p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.7;
}

.storyboard-script-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.storyboard-script-card-shell {
  display: grid;
  gap: 8px;
}

.storyboard-script-card-shell--active,
.storyboard-script-card-shell--editing {
  z-index: 1;
}

.storyboard-insert-button {
  width: 100%;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f766e;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease, color 0.18s ease, transform 0.18s ease;
  display: inline-flex;
  align-items: center;
  min-height: 22px;
}

.storyboard-insert-button::before,
.storyboard-insert-button::after {
  content: '';
  width: 96px;
  height: 1px;
  background: linear-gradient(90deg, rgba(125, 211, 192, 0), rgba(125, 211, 192, 0.66), rgba(125, 211, 192, 0));
}

.storyboard-insert-button__icon {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1;
}

.storyboard-script-card-shell:hover .storyboard-insert-button,
.storyboard-script-card-shell--active .storyboard-insert-button,
.storyboard-script-card-shell--editing .storyboard-insert-button {
  opacity: 1;
  pointer-events: auto;
}

.storyboard-insert-button:hover {
  color: #0f9f8c;
  transform: translateY(-1px);
}

.storyboard-script-card {
  position: relative;
  display: grid;
  gap: 16px;
  padding: 22px;
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.storyboard-script-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(59, 130, 246, 0.06));
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.storyboard-script-card:hover,
.storyboard-script-card--active {
  transform: translateY(-3px);
  border-color: rgba(16, 185, 129, 0.46);
  box-shadow: 0 24px 44px rgba(15, 23, 42, 0.1);
}

.storyboard-script-card:hover::before,
.storyboard-script-card--active::before,
.storyboard-script-card--editing::before {
  opacity: 1;
}

.storyboard-script-card--editing {
  border-color: rgba(53, 109, 255, 0.32);
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.98), rgba(255, 255, 255, 0.98));
}

.storyboard-script-card__header,
.storyboard-script-card__title-row,
.storyboard-script-card__actions,
.storyboard-script-card__editor-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.storyboard-script-card__badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.storyboard-script-card__badge {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.storyboard-script-card__badge--scene {
  background: rgba(16, 185, 129, 0.16);
  color: #047857;
}

.storyboard-script-card__body,
.storyboard-script-card__editor {
  display: grid;
  gap: 14px;
}

.storyboard-script-card__grid,
.storyboard-script-card__binding-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.storyboard-script-card__title-row strong {
  font-size: 24px;
  line-height: 1.25;
  color: #0f172a;
}

.storyboard-script-card__title-row span {
  color: #64748b;
}

.storyboard-script-card__section {
  display: grid;
  gap: 8px;
}

.storyboard-script-card__section span {
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.storyboard-script-card__section p {
  margin: 0;
  line-height: 1.75;
  color: #334155;
}

.storyboard-script-card__binding-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.storyboard-script-card__binding-tag {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(14, 116, 144, 0.1);
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 960px) {
  .storyboard-script-card__grid,
  .storyboard-script-card__binding-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .storyboard-script-toolbar,
  .storyboard-script-card__header {
    flex-direction: column;
  }
}
</style>